import {yupResolver} from "@hookform/resolvers/yup";
import {ReactNode} from "react";
import {FormProvider, useForm} from "react-hook-form";

import {FormModal} from "@/components/form/overlay/FormModal.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {useNavigationBlocker} from "@/lib/hooks/useNavigationBlocker.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {ContactGroupBaseItems} from "@/lib/types/contacts.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {EditIntegrationForm, EditIntegrationInfoSchema} from "@/pages/integrations/edit/EditIntegrationForm.tsx";

interface EditIntegrationModalProps {
  formId: string;
  title: ReactNode;
  defaultValues?: IntegrationFormData;
  availableContactGroups: ContactGroupBaseItems;
  onSubmit: (data: IntegrationFormData, isDirty: boolean) => Promise<void>;
  onCancel?: () => void;
  show: boolean;
  serverErrors?: ServerErrors;
  hide: () => void;
}

const hasDirtyField = (fields: object): boolean =>
  Object.values(fields).some((value) =>
    typeof value === "object" && value !== null ? hasDirtyField(value) : value === true
  );

// Mounted only while shown: one EditIntegrationModal exists per list row/card, and
// react-router supports a single active useBlocker — keeping hidden instances mounted
// would register a blocker per row and only the last one would work
export const EditIntegrationModal = (props: EditIntegrationModalProps) => {
  if (!props.show) return null;
  return <EditIntegrationModalContent {...props} />;
};

const EditIntegrationModalContent = ({
  formId,
  hide,
  defaultValues,
  availableContactGroups,
  onSubmit,
  show,
  title,
  serverErrors,
}: EditIntegrationModalProps) => {
  // pre-select the first contact group via defaultValues rather than setValue so
  // the auto-assignment doesn't count towards isDirty and trip the unsaved-changes blocker
  const defaultContactGroups = defaultValues?.contact_groups.length
    ? defaultValues.contact_groups
    : availableContactGroups.items.length
      ? [[...availableContactGroups.items].sort((a, b) => a.id - b.id)[0].id]
      : [];

  const methods = useForm<IntegrationFormData>({
    resolver: yupResolver<IntegrationFormData>(EditIntegrationInfoSchema),
    defaultValues: {...defaultValues, contact_groups: defaultContactGroups},
  });
  // block on dirtyFields rather than isDirty: module settings fields without an
  // entry in defaultValues (e.g. an unchecked checkbox) register as false/"" and
  // make the computed isDirty true without any user input; dirtyFields is only
  // populated by actual changes
  const {dirtyFields, isSubmitSuccessful} = methods.formState;

  // isSubmitSuccessful goes true whenever the submit handler resolves — including a
  // server-rejected save, which returns early instead of throwing — so keep blocking
  // while the last save produced serverErrors
  const {showBlockerModal, NavigationBlockerModal} = useNavigationBlocker({
    shouldBlock: hasDirtyField(dirtyFields) && (!isSubmitSuccessful || !!serverErrors),
    onConfirm: hide,
    onUnblocked: hide,
  });

  return (
    <UpErrorBoundary>
      <FormProvider {...methods}>
        <FormModal
          title={title}
          submitBtnText={defaultValues?.id ? "Save" : "Add New"}
          onCancel={showBlockerModal}
          show={show}
          size="lg"
          formId={formId}
          hasRequiredFields
          // mount-time autoFocus is unreliable while the modal is animating in;
          // focus the marked field once the enter transition has finished
          onEntered={(node) => node.querySelector<HTMLElement>("[data-autofocus]")?.focus()}
        >
          <EditIntegrationForm
            availableContactGroups={availableContactGroups}
            formId={formId}
            onSubmit={onSubmit}
            onError={console.log}
            serverErrors={serverErrors}
          />
        </FormModal>
        <NavigationBlockerModal />
      </FormProvider>
    </UpErrorBoundary>
  );
};
